import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { getUserFaith, updateUserFaith } from "../../Services/subscription";
import { getFaithProfile, deleteFaithProfile, getFaithSchema } from "../../Services/faithProfile";
import GenericModal from "../../Components/Modal/GenericModal";
import FaithBeliefSettings from "../Settings/FaithBeliefSettings";
import FaithWizard from "./FaithWizard";
import { ReactComponent as SmallScreenLeftArrow } from "../../Assets/icon/arrow-narrow-left-white.svg";
import { ReactComponent as LeftArrow } from "../../Assets/icon/arrow-narrow-left.svg";
import { Pencil } from "lucide-react";
import { resolveReviewRow, buildLabelResolver } from "../../Utils/faithProfile";

import "../../Pages/TermsofUse/style.css";
import "../Settings/style.css";
import "./style.css";

function FaithSpirituality() {
  const navigate = useNavigate();
  const { setHeaderContent, setbeliefSystem } = useHeader();

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [faithData, setFaithData] = useState({ faith: "", toggle_state: false, status: "" });
  const [faithLoading, setFaithLoading] = useState(true);
  const [faithError, setFaithError] = useState("");
  const [isToggleUpdating, setIsToggleUpdating] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [schema, setSchema] = useState(null);

  const [isFaithModalOpen, setIsFaithModalOpen] = useState(false);
  const [faithSaving, setFaithSaving] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const faithFormRef = useRef(null);

  useEffect(() => {
    setHeaderContent(false);
  }, [setHeaderContent]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadFaithData = useCallback(async () => {
    setFaithLoading(true);
    setFaithError("");
    try {
      const response = await getUserFaith();
      const payload = response?.payload ?? response;
      setFaithData({
        faith: payload?.faith || "",
        toggle_state: Boolean(payload?.toggle_state),
        status: payload?.status || payload?.subscription_status || "",
      });
      if (typeof setbeliefSystem === "function") setbeliefSystem(payload || {});
    } catch (error) {
      setFaithError("Unable to load your faith settings. Please try again.");
    } finally {
      setFaithLoading(false);
    }
  }, [setbeliefSystem]);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const res = await getFaithProfile();
      setProfile(res?.data?.profile ?? null);
    } catch (error) {
      // Non-blocking: the hub still works for tradition + toggle if the
      // personalization service is unreachable.
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const loadSchema = useCallback(async () => {
    try {
      const result = await getFaithSchema("en");
      setSchema(result || null);
    } catch (error) {
      // Endpoint always returns a valid schema (server falls back to a default),
      // so a failure here is transport/auth — leave schema null and gate the
      // personalization entry point.
      setSchema(null);
    }
  }, []);

  useEffect(() => {
    loadFaithData();
    loadProfile();
    loadSchema();
  }, [loadFaithData, loadProfile, loadSchema]);

  // Enable -> flip the entitlement toggle (core-lambda).
  // Disable -> flip the toggle AND delete the aris-ai profile so reminders are
  // cancelled (contract §5.2 — two calls).
  const handleToggleChange = async (event) => {
    const enabled = event.target.checked;
    if (isToggleUpdating) return;

    setIsToggleUpdating(true);
    setFaithError("");
    setFaithData((prev) => ({ ...prev, toggle_state: enabled }));
    try {
      await updateUserFaith({ faith: faithData?.faith || "", toggle_state: enabled });
      if (!enabled) {
        try {
          await deleteFaithProfile();
        } catch (_) {
          /* server mirrors enabled->toggle_state as a safety net */
        }
      }
      await loadFaithData();
      await loadProfile();
    } catch (error) {
      setFaithError(
        error?.response?.data?.message || error?.message || "Unable to update faith settings.",
      );
      await loadFaithData();
    } finally {
      setIsToggleUpdating(false);
    }
  };

  const handleFaithModalClose = () => {
    setIsFaithModalOpen(false);
    loadFaithData();
  };

  const handleHeaderBack = () => {
    if (showWizard) {
      setShowWizard(false);
      return;
    }
    navigate("/dashboard");
  };

  const handleWizardSaved = () => {
    loadProfile();
    loadFaithData();
  };

  const hasProfile = Boolean(profile);

  if (showWizard && schema) {
    return (
      <FaithWizard
        schema={schema}
        initialProfile={profile}
        faithTradition={faithData?.faith || ""}
        onClose={() => setShowWizard(false)}
        onSaved={handleWizardSaved}
      />
    );
  }

  return (
    <div className="settings-container faith-hub">
      <header className="termsofuse-header">
        <div className="termsofuse-gradient-background ">
          <div className="termsofuse-nav-container">
            <div className="termsofuse-logo-container " onClick={handleHeaderBack}>
              {screenWidth < 480 ? (
                <SmallScreenLeftArrow className="termsofuse-back-button" />
              ) : (
                <LeftArrow className="termsofuse-back-button" />
              )}
            </div>
            <h1 className="head-not-txt">Faith &amp; Spirituality</h1>
            <div className="termsofuse-space"></div>
          </div>
        </div>
      </header>

      <div className="settings-hero-container">
        <div className="faith-hub-content">
          {faithError && <div className="settings-error-banner">{faithError}</div>}

          {/* Guidance toggle + tradition (core-lambda) */}
          <div className="faith-hub-card">
            {faithLoading ? (
              <div className="settings-card-loading">Loading your faith settings…</div>
            ) : (
              <div className="faith-tradition">
                <div className="faith-tradition-toggle-row">
                  <span className="faith-tradition-heading">
                    Include Faith &amp; Spirituality in Guidance
                  </span>
                  <label className={`settings-toggle ${isToggleUpdating ? "disabled" : ""}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(faithData?.toggle_state)}
                      onChange={handleToggleChange}
                      disabled={isToggleUpdating}
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                </div>
                <div className="faith-tradition-edit-row">
                  <span className="faith-tradition-label">Faith Tradition</span>
                  <button
                    type="button"
                    className="settings-inline-edit faith-tradition-edit"
                    onClick={() => setIsFaithModalOpen(true)}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                </div>
                <div className={`faith-tradition-value ${faithData?.faith ? "" : "muted"}`}>
                  {faithData?.faith || "Not Selected"}
                </div>
              </div>
            )}
          </div>

          {/* Personalization summary / entry point (aris-ai wizard) */}
          {faithData?.toggle_state && !faithLoading && (
            <div className="faith-personalization-card">
              <h2 className="faith-personalization-title">Personalization</h2>
              {profileLoading ? (
                <div className="settings-card-loading">Loading…</div>
              ) : hasProfile ? (
                <>
                  <PersonalizationSummary schema={schema} profile={profile} />
                  <button
                    type="button"
                    className="faith-btn faith-btn-primary"
                    onClick={() => setShowWizard(true)}
                    disabled={!schema}
                  >
                    Update
                  </button>
                </>
              ) : (
                <>
                  <p className="faith-personalization-empty">
                    You haven't personalized your faith experience yet. Set how Aris weaves
                    your practice into coaching, prayer reminders and holidays.
                  </p>
                  <button
                    type="button"
                    className="faith-btn faith-btn-primary"
                    onClick={() => setShowWizard(true)}
                    disabled={!schema}
                  >
                    Personalize
                  </button>
                </>
              )}
            </div>
          )}

          {!faithData?.toggle_state && !faithLoading && (
            <div className="faith-hub-hint">
              Turn on Faith &amp; Spirituality above to personalize your experience.
            </div>
          )}
        </div>

        <GenericModal
          isOpen={isFaithModalOpen}
          onClose={handleFaithModalClose}
          heading="Your Faith Tradition"
          primaryLabel="Save"
          secondaryLabel="Cancel"
          onPrimary={() => faithFormRef.current?.save?.()}
          onSecondary={handleFaithModalClose}
          loading={faithSaving}
        >
          <FaithBeliefSettings ref={faithFormRef} onClose={handleFaithModalClose} onLoadingChange={setFaithSaving} />
        </GenericModal>
      </div>
    </div>
  );
}

// Read-only summary of the saved profile, rendered from the schema's review
// rows (same source/transform rules as the wizard's Review screen).
function PersonalizationSummary({ schema, profile }) {
  const resolveLabel = buildLabelResolver(schema);
  const rows = schema?.review?.rows || [];
  return (
    <div className="faith-summary">
      {rows.map((row, i) => {
        const { label, lines } = resolveReviewRow(row, profile, resolveLabel);
        return (
          <div className="faith-summary-item" key={i}>
            <div className="faith-summary-label">{label}</div>
            {lines.map((line, j) => (
              <div className="faith-summary-value" key={j}>
                {line}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default FaithSpirituality;
